import { localQueryEntityOptions } from '@app/icare/client/db/client/localQueryOneOptions'
import { sync } from '@app/icare/client/sync'
import type { BiaMeasurementForm } from '@app/icare/db/schema/form/BiaMeasurement'
import type { PatientFormIdType } from '@app/icare/db/schema/form/PatientFormCommon'
import { PatientFormId } from '@app/icare/db/schema/form/PatientFormCommon'
import type { DiagnosisSelect, PatientIdType } from '@app/icare/entities/patient/schema/Patient'
import { PatientId } from '@app/icare/entities/patient/schema/PatientId'
import type { PatientMobilityRelation } from '@app/icare/entities/patient/schema/PatientMobility'
import { PatientMobilityId } from '@app/icare/entities/patient/schema/PatientMobility'
import { appFormOptions } from '@app/icare/form/appFormOptions'
import { useAppForm } from '@app/icare/form/useAppForm'
import { t } from '@lingui/core/macro'
import { zodParams } from '@pkg/db/utils'
import type { EditablePolicy } from '@pkg/form/contexts/editableForm'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { ConfigProvider, Form } from 'antd'
import dayjs from 'dayjs'
import { useState } from 'react'
import { z } from 'zod'
import { EditBiaMeasurement } from './-edit'

const areDiagnosesEqual = (left: DiagnosisSelect[] | undefined, right: DiagnosisSelect[] | undefined): boolean => {
  return JSON.stringify(left ?? []) === JSON.stringify(right ?? [])
}

type CustomMealsValue = NonNullable<NonNullable<BiaMeasurementForm['form']>['foodIntakePerDay']>['customMeals']
type NotificationsValue = NonNullable<
  NonNullable<NonNullable<BiaMeasurementForm['form']>['verification']>['notifications']
>

const normalizeCustomMeals = (value: unknown): CustomMealsValue => {
  if (value == null) {
    return value as CustomMealsValue
  }

  if (Array.isArray(value)) {
    return value as CustomMealsValue
  }

  if (typeof value !== 'object') {
    return undefined
  }

  const objectValue = value as Record<string, unknown>

  const asArray = Object.entries(objectValue)
    .filter(([key]) => /^\d+$/.test(key))
    .sort((left, right) => Number(left[0]) - Number(right[0]))
    .map(([, entry]) => entry)

  return asArray as CustomMealsValue
}

const normalizeSubmitValue = (value: BiaMeasurementForm): BiaMeasurementForm => {
  const currentMeals = value.form?.foodIntakePerDay?.customMeals
  const normalizedMeals = normalizeCustomMeals(currentMeals)
  const rawStatusChangedAt: unknown = value.statusChangedAt
  const normalizedStatusChangedAt =
    rawStatusChangedAt instanceof Date ? rawStatusChangedAt.toISOString() : value.statusChangedAt

  const currentNotifications = value.form?.verification?.notifications
  const normalizedNotifications = (() => {
    if (currentNotifications == null) {
      return currentNotifications
    }

    const notificationRecord = currentNotifications as Record<string, unknown>
    const normalizeNotificationEntry = (entry: unknown) => {
      if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
        return entry
      }
      return {}
    }

    return {
      ...notificationRecord,
      Doctor: normalizeNotificationEntry(notificationRecord.Doctor),
      Clinic: normalizeNotificationEntry(notificationRecord.Clinic),
      Other: normalizeNotificationEntry(notificationRecord.Other),
    } as NotificationsValue
  })()

  if (
    currentMeals === normalizedMeals &&
    normalizedStatusChangedAt === value.statusChangedAt &&
    normalizedNotifications === currentNotifications
  ) {
    return value
  }

  return {
    ...value,
    statusChangedAt: normalizedStatusChangedAt,
    form: {
      ...value.form,
      verification: {
        ...value.form?.verification,
        notifications: normalizedNotifications,
      },
      foodIntakePerDay: {
        ...value.form?.foodIntakePerDay,
        customMeals: normalizedMeals,
      },
    },
  }
}

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/_forms/bia-messung/$form/')({
  component: RouteComponent,
  params: zodParams(z.object({ id: PatientId.ref, form: z.string() })),
  staticData: {
    layout: {
      menuRender: false,
    },
  },
  loader: async ({ params, context: { queryClient } }) => {
    if (params.form === 'new') return

    await queryClient.ensureQueryData(localQueryEntityOptions(params.id))
    await queryClient.ensureQueryData(localQueryEntityOptions(params.form as PatientFormIdType))
  },
})

function RouteComponent() {
  const router = useRouter()
  const { id, form: formId } = Route.useParams() as { id: PatientIdType; form: PatientFormIdType | 'new' }

  const isNew = formId === 'new'
  const [isEditing, setIsEditing] = useState(isNew)

  const patient = useQuery(localQueryEntityOptions(id))
  const current = useQuery(localQueryEntityOptions(formId))
  const editModeAllowed = current.data?.status === 'processing'
  const editablePolicy: Partial<EditablePolicy> = isNew
    ? { forceMode: 'edit' as const }
    : editModeAllowed
      ? { forceMode: isEditing ? 'edit' : ('read' as const) }
      : { forceMode: 'read' as const }
  const isFormInEditMode = editablePolicy?.forceMode === 'edit'

  const form = useAppForm(
    appFormOptions({
      defaultValues: (current.data as BiaMeasurementForm) ?? {
        patientId: id,
        type: 'BiaMeasurement',
        status: 'new',
        id: PatientFormId.generate(),
        form: {},
      },
      validators: {
        // onChange: BiaMeasurementFormSchema as any,
      },
      toast: { subject: t`BIA-Messung` },
      onSubmit: async ({ value }) => {
        const submitValue = normalizeSubmitValue(value)

        await sync.mutate(submitValue)

        if (isNew) {
          const mobility = submitValue.form?.general?.mobility
          if (mobility) {
            await sync.mutate({
              id: PatientMobilityId.generate(),
              patientId: submitValue.patientId,
              mobility,
              activeFrom: dayjs().format('YYYY-MM-DD'),
            } as PatientMobilityRelation)
          }

          const nextDiagnosis = submitValue.form?.anamnesis?.diagnosis ?? []
          const currentDiagnosis = patient.data?.anamnesis?.diagnosis ?? []

          if (patient.data && !areDiagnosesEqual(currentDiagnosis, nextDiagnosis)) {
            await sync.mutate({
              ...patient.data,
              anamnesis: {
                ...patient.data.anamnesis,
                diagnosis: nextDiagnosis,
              },
            })
          }
          await router.navigate({ to: '/patienten/$id/bia-messung', replace: true, params: { id } })
        }

        form.reset(submitValue)
      },
    }),
  )

  return (
    <form.AppForm>
      <form.ProForm editablePolicy={editablePolicy} submitter={false}>
        <ConfigProvider
          theme={{
            components: {
              Form: {
                itemMarginBottom: isFormInEditMode ? 10 : 0,
              },
            },
          }}
        >
          <Form component={false} layout="horizontal" labelCol={{ flex: '280px' }} wrapperCol={{ flex: '1 1 0' }}>
            <EditBiaMeasurement
              isNew={isNew}
              isEditModeAllowed={editModeAllowed}
              isEditing={isFormInEditMode}
              onEditModeChange={setIsEditing}
            />
          </Form>
        </ConfigProvider>
      </form.ProForm>
    </form.AppForm>
  )
}
